import { defineStore } from "pinia";
import { ref } from "vue";

import { api, ApiError } from "@/api/client";
import type { OperatorRole, OperatorView } from "@/api/types";

// State machine:
// - unknown  → boot-time, haven't tried /api/me yet
// - probing  → /api/me request inflight
// - anonymous→ confirmed not logged in (401)
// - ready    → authenticated, current operator known
// - error    → /api/me failed for a non-auth reason (network/5xx)
type AuthState =
  | { kind: "unknown" }
  | { kind: "probing" }
  | { kind: "anonymous" }
  | { kind: "ready"; operator: OperatorView }
  | { kind: "error"; message: string };

export const useAuthStore = defineStore("auth", () => {
  const state = ref<AuthState>({ kind: "unknown" });
  let probeInflight: Promise<void> | null = null;

  // GET /api/me. Called on app mount and after login/logout. 401 is a
  // success path — it just means "anonymous". Anything else surfaces as
  // an error state the UI can flag.
  async function probe(): Promise<void> {
    if (probeInflight) return probeInflight;
    if (state.value.kind === "ready") return;
    state.value = { kind: "probing" };
    const attempt = state.value;
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 15_000);
    probeInflight = (async () => {
      try {
        const operator = await api.me(controller.signal);
        if (state.value !== attempt) return;
        state.value = { kind: "ready", operator };
      } catch (err) {
        // A request started before login/logout must not undo its result.
        if (state.value !== attempt) return;
        if (err instanceof ApiError && err.status === 401) {
          state.value = { kind: "anonymous" };
        } else {
          state.value = { kind: "error", message: describeError(err) };
        }
      } finally {
        clearTimeout(timeout);
        probeInflight = null;
      }
    })();
    return probeInflight;
  }

  async function login(email: string, password: string): Promise<void> {
    const resp = await api.login(email, password);
    state.value = { kind: "ready", operator: resp.operator };
  }

  async function logout(): Promise<void> {
    try {
      await api.logout();
    } catch (err) {
      // 401 is fine — session already gone; anything else we log and proceed.
      if (!(err instanceof ApiError) || err.status !== 401) {
        console.warn("logout failed", err);
      }
    } finally {
      state.value = { kind: "anonymous" };
    }
  }

  // Force the local state to anonymous without an API round-trip — used by the
  // global 401 handler when the server reports the session is gone.
  function markAnonymous(): void {
    state.value = { kind: "anonymous" };
  }

  function operator(): OperatorView | null {
    return state.value.kind === "ready" ? state.value.operator : null;
  }

  function hasRole(roles: OperatorRole[]): boolean {
    const op = operator();
    return op !== null && roles.includes(op.role);
  }

  function isAdminOrAbove(): boolean {
    return hasRole(["owner", "admin"]);
  }

  function isAuthenticated(): boolean {
    return state.value.kind === "ready";
  }

  return {
    state,
    probe,
    login,
    logout,
    markAnonymous,
    operator,
    hasRole,
    isAdminOrAbove,
    isAuthenticated,
  };
});

function describeError(err: unknown): string {
  if (err instanceof ApiError) return `${err.code} (HTTP ${err.status})`;
  if (err instanceof Error) return err.message;
  return "unknown error";
}
